/**
 * @file Span Duration Utilities
 * @description Utility functions to calculate span durations in milliseconds from OpenTelemetry time formats.
 */

/**
 * Calculates the duration between a start and end time in milliseconds.
 *
 * @param {[number, number]} startTime - Start time as [seconds, nanoseconds].
 * @param {[number, number]} endTime - End time as [seconds, nanoseconds].
 * @returns {number} Duration in milliseconds.
 */
const getSpanDurationMs = (startTime, endTime) => {
    const startNs = startTime[0] * 1e9 + startTime[1];
    const endNs = endTime[0] * 1e9 + endTime[1];

    return (endNs - startNs) / 1e6;
};
/**
 * Converts a time value from [seconds, nanoseconds] to milliseconds.
 *
 * @param {[number, number]} time - Time value as [seconds, nanoseconds].
 * @returns {number} Time in milliseconds.
 */
const convertToMilliseconds = ([seconds, nanoseconds]) => {
    return seconds * 1000 + nanoseconds / 1e6;
};
/**
 * Returns the duration of a span in milliseconds. If the span has a _duration field, it is used directly.
 * Otherwise, the duration is calculated from startTime and endTime.
 *
 * @param {Object} span - The OpenTelemetry span object.
 * @param {[number, number]} [span._duration] - Optional pre-calculated duration as [seconds, nanoseconds].
 * @param {[number, number]} span.startTime - Start time as [seconds, nanoseconds].
 * @param {[number, number]} span.endTime - End time as [seconds, nanoseconds].
 * @returns {number} Duration of the span in milliseconds.
 */
export const getDuration = (span) => {
    if (span._duration) {
        return convertToMilliseconds(span._duration);
    }

    return getSpanDurationMs(span.startTime, span.endTime);
};
